<template>
  <NPopper v-model="isOpenAddWatchlist" offset-y="8">
    <div
      v-gid="{
        module: 'watchlist',
        screen: 'home',
        section: 'symbolSearch',
        component: 'watchlistButton',
      }"
      @click.stop
    >
      <NPopper hover>
        <NegarStarBoldIcon
          v-if="usedWatchlistIds.length"
          color="green"
          class="cursor-pointer"
        />

        <NegarStarLightIcon v-else class="cursor-pointer" />

        <template #content>
          <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
            <NText
              variant="caption2"
              weight="medium"
              class="text-neutral-quaternary"
            >
              {{ t('modules.watchlist.addSymbolToWatchlist') }}
            </NText>
          </div>
        </template>
      </NPopper>
    </div>

    <template #content>
      <div class="relative bg-neutral-primary rounded-md shadow-maxi w-52">
        <div
          class="max-h-[224px] w-52 overflow-y-hidden hover:overflow-y-scroll overflow-x-hidden"
        >
          <div
            v-for="(watchlistData, index) of watchListOverview"
            :key="index"
            class="p-2 border-b border-neutral-secondary hover:bg-neutral-secondary active:bg-neutral-primary-press cursor-pointer last:border-none"
          >
            <div
              v-if="hasAlreadyAdded(watchlistData.id)"
              v-gid="{
                module: 'watchlist',
                screen: 'home',
                section: 'addToWatchlistDropDown',
                component: 'removeFromWatchlistButton',
              }"
              class="flex items-center"
              @click.stop="
                handleRemoveFromWatchlist(watchlistData.id, watchlistData.title)
              "
            >
              <NegarStarBoldIcon color="green" />

              <NText weight="medium" variant="caption1" class="ms-2 text-right">
                {{ watchlistData.title }}
              </NText>
            </div>

            <div
              v-else
              v-gid="{
                module: 'watchlist',
                screen: 'home',
                section: 'addToWatchlistDropDown',
                component: 'addToWatchlistButton',
              }"
              class="flex items-center"
              @click.stop="
                handleAddToWatchlist(watchlistData.id, watchlistData.title)
              "
            >
              <NegarStarLightIcon />

              <NText weight="medium" variant="caption1" class="ms-2 text-right">
                {{ watchlistData.title }}
              </NText>
            </div>
          </div>
        </div>

        <div
          v-gid="{
            module: 'watchlist',
            screen: 'home',
            section: 'addToWatchlistDropDown',
            component: 'createWatchlistButton',
          }"
          class="cursor-pointer flex justify-center bg-neutral-primary items-center border-t border-neutral-secondary p-2"
          :class="[watchListOverview.length ? 'rounded-b-md' : 'rounded-md']"
          @click.stop="openCreateWatchlistDialog"
        >
          <NegarPlusOutlineIcon size="16" class="text-brand-primary ml-1" />

          <NText variant="caption1" weight="medium" class="text-brand-primary">
            {{ t('modules.watchlist.create_title') }}
          </NText>
        </div>
      </div>
    </template>
  </NPopper>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast, useDialog } from '@negar/core';
import {
  NegarStarLightIcon,
  NegarStarBoldIcon,
  NegarPlusOutlineIcon,
} from '@negar/icons';
import {
  useRemoveSymbolFromWatchlist,
  useAddSymbolToWatchlist,
  WATCHLIST_QUERY_KEYS,
  useGetWatchlistOverview,
  useGetWatchlist,
} from '@core/watchlist';
import { useQueryClient } from '@libs/use';
import { Watchlist } from '@libs/api-reference';

import { CreateWatchlistDialog } from '@/modules/watchlist';
import { useActiveSymbolIsin, useLiveSymbolQuery } from '@/modules/symbol';

const selectedWatchlist = ref<{
  name: string;
  id: string;
} | null>(null);
const isOpenAddWatchlist = ref<boolean>(false);

const usedWatchlistIds = computed(() => {
  const selectedSymbolIsin = activeSymbol.value?.isin;

  if (selectedSymbolIsin) {
    return watchListOverview.value
      .filter((watchlist) => watchlist.isins?.includes(selectedSymbolIsin))
      .map(({ id }) => id);
  }

  return [];
});

const { t } = useI18n();

const toast = useToast();
const { add: addDialog } = useDialog();

const queryClient = useQueryClient();

const { activeIsin } = useActiveSymbolIsin();
const { symbol: activeSymbol } = useLiveSymbolQuery(activeIsin);

const { watchListOverview, getWatchlistOverview } = useGetWatchlistOverview();
const { getWatchlist } = useGetWatchlist();
const { addToWatchlist } = useAddSymbolToWatchlist({
  onSuccess: () => {
    getWatchlistOverview();

    queryClient.invalidateQueries(
      WATCHLIST_QUERY_KEYS.watchlistItems(selectedWatchlist.value?.id)
    );

    toast.neutral({
      message: t('modules.watchlist.add_symbol_to_watchlist_message', {
        symbolName: activeSymbol.value?.instrumentName,
        watchlistName: selectedWatchlist.value?.name,
      }),
      duration: 3000,
    });

    selectedWatchlist.value = null;
  },
  onReject: () => {
    toast.negative({
      message: t('modules.watchlist.add_symbol_to_watchlist_failed_message', {
        symbolName: activeSymbol.value?.instrumentName,
        watchlistName: selectedWatchlist.value?.name,
      }),
      duration: 3000,
    });
  },
});

const { removeSymbolFromWatchlist } = useRemoveSymbolFromWatchlist({
  onSuccess: () => {
    getWatchlistOverview();

    queryClient.invalidateQueries(
      WATCHLIST_QUERY_KEYS.watchlistItems(selectedWatchlist.value?.id)
    );

    toast.neutral({
      message: t('modules.watchlist.removeSymbolFromWatchlistMessage', {
        symbolName: activeSymbol.value?.instrumentName,
        watchlistName: selectedWatchlist.value?.name,
      }),
      duration: 3000,
    });

    selectedWatchlist.value = null;
  },
  onReject: () => {
    toast.negative({
      message: t('modules.watchlist.removeSymbolFromWatchlistFailedMessage', {
        symbolName: activeSymbol.value?.instrumentName,
        watchlistName: selectedWatchlist.value?.name,
      }),
      duration: 3000,
    });
  },
});

const handleAddToWatchlist = async (id, name) => {
  selectedWatchlist.value = { id, name };

  addToWatchlist({
    watchlistId: id,
    isinCode: activeSymbol.value?.isin,
  });
};

const handleRemoveFromWatchlist = (id, name) => {
  selectedWatchlist.value = { id, name };

  removeSymbolFromWatchlist({
    watchlistId: id,
    isin: activeSymbol.value?.isin,
  });
};

const openCreateWatchlistDialog = () => {
  isOpenAddWatchlist.value = false;

  addDialog({
    title: t('modules.watchlist.create_title'),
    bodyClass: '!w-1/5',
    content: computed(() => ({
      component: CreateWatchlistDialog,
      props: {
        onAfterCreate: handleAfterCloseAddDialog,
      },
    })),
  });
};

const handleAfterCloseAddDialog = (newWatchlist: Watchlist) => {
  getWatchlistOverview();
  getWatchlist();

  addToWatchlist({
    watchlistId: newWatchlist.id,
    isinCode: activeSymbol.value?.isin,
  });
};

const hasAlreadyAdded = (watchlistId: string) =>
  usedWatchlistIds.value.includes(watchlistId);
</script>
